var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};

// src/common.ts
import { designReducer } from "@ballpark/design-creator";
import { getDoc } from "@ballpark/realtime-crdt";
import { runInAction } from "mobx";
var reduxActionToMobx = (design, reduxAction) => transact(design, () => designReducer(design, reduxAction));
var transact = (design, fn, origin) => {
  const document = getDoc(design);
  document.transact(() => runInAction(fn), origin);
};
var transaction = (design, fn, origin) => (...args) => transact(design, () => fn(...args), origin);

// src/react.tsx
import { getDoc as getDoc2 } from "@ballpark/realtime-crdt";
import { runInAction as runInAction2 } from "mobx";
import { enableStaticRendering } from "mobx-react";
import {
  createContext,
  useCallback,
  useContext
} from "react";
import { useMemoOne } from "use-memo-one";

// src/actions.ts
import {
  actions as reduxActions
} from "@ballpark/design-creator";
function createActions(design) {
  const entries = Object.entries(reduxActions);
  return entries.reduce((acc, [key, actionCreator]) => {
    acc[key] = (...args) => reduxActionToMobx(design, actionCreator(...args));
    return acc;
  }, {});
}

// src/selectors.ts
import { selectors } from "@ballpark/design-creator";
function createBoundSelectors(design) {
  const entries = Object.entries(selectors);
  return entries.reduce((acc, [key, selector]) => {
    acc[key] = (...args) => selector(design, ...args);
    return acc;
  }, {});
}

// src/state.ts
import { defaultState } from "@ballpark/design-creator";
import {
  $$DOC,
  createProxiedDoc,
  createProxyFromDoc
} from "@ballpark/realtime-crdt";
import { proxyOptions } from "@ballpark/realtime-crdt-mobx";
function createEmptyProxiedDesign() {
  return createProxiedDoc(void 0, "design", proxyOptions);
}
function createProxiedDesign(design = defaultState) {
  return createProxiedDoc(design, "design", proxyOptions);
}
function docToProxiedDesign(doc) {
  return createProxyFromDoc(doc, "design", "map", proxyOptions);
}
function isProxiedDesign(design) {
  return $$DOC in design;
}

// src/react.tsx
import { Fragment as Fragment2, jsx } from "react/jsx-runtime";
var isSSR = typeof window === "undefined";
enableStaticRendering(isSSR);
function createBoundDesignData(design) {
  return __spreadValues(__spreadValues({
    // state
    design,
    transact: (fn, origin) => transact(design, fn, origin),
    transaction: (fn, origin) => transaction(design, fn, origin)
  }, createActions(design)), createBoundSelectors(design));
}
var DesignContext = createContext(null);
function useProxiedDesign(design) {
  const proxiedDesign = useMemoOne(
    () => isProxiedDesign(design) ? design : createProxiedDesign(design),
    [design]
  );
  return proxiedDesign;
}
function DesignProvider({
  children,
  design
}) {
  const proxiedDesign = useProxiedDesign(design);
  const designData = useMemoOne(
    () => createBoundDesignData(proxiedDesign),
    [proxiedDesign, design]
  );
  return /* @__PURE__ */ jsx(DesignContext.Provider, { value: designData, children });
}
function WeakDesignProvider({
  children,
  design
}) {
  const state = useContext(DesignContext);
  if (state === null) {
    return /* @__PURE__ */ jsx(DesignProvider, { design, children });
  }
  return /* @__PURE__ */ jsx(Fragment2, { children });
}
function useDesign() {
  const state = useContext(DesignContext);
  if (state === null)
    throw new Error("useDesign() called outside of DesignProvider");
  return state;
}
function useTransact() {
  const { design } = useDesign();
  return useMemoOne(() => transact.bind(void 0, design), [design]);
}
function useTransaction() {
  const { design } = useDesign();
  return useMemoOne(
    () => transaction.bind(void 0, design),
    [design]
  );
}
function useTransactionCallback(fn, dependencies, origin) {
  const { design } = useDesign();
  const document = getDoc2(design);
  return useCallback(
    (...args) => {
      document.transact(() => runInAction2(() => fn(...args)), origin);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [document, ...dependencies]
  );
}
export {
  DesignProvider,
  WeakDesignProvider,
  createEmptyProxiedDesign,
  createProxiedDesign,
  docToProxiedDesign,
  isProxiedDesign,
  reduxActionToMobx,
  transact,
  transaction,
  useDesign,
  useProxiedDesign,
  useTransact,
  useTransaction,
  useTransactionCallback
};
